"use client";

import { useSession } from "next-auth/react";
import { useEffect, useRef, useState } from "react";
import { LuQrCode } from "react-icons/lu";
import { AttendanceStatus } from "@prisma/client";
import { useQuery } from "@tanstack/react-query";

import { usePermissionsContext } from "~/app/providers";
import { cn } from "~/utils/fontsConfig";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
} from "~/components/v3/shadcn/Card";
import { dedupe } from "~/utils/various";
import { If } from "~/components/v3/Conditional";
import { Button } from "~/components/v3/themed/Button";
import CommunityHeads from "~/components/v3/community-components/CommunityHeads";
import { EventAttendeesSheet } from "~/components/v3/events-components/EventAttendeesSheet";

import { CapacityBar } from "./CapacityBar";
import { GetEventFromSlug } from "../actions";
import { PermissionEnum } from "~/types/PermissionTypes";
import { refetchAction } from "./rsvp_components/actions";
import { getProximityAndFollowersInvites } from "~/app/events/[slug]/invites/actions";
import { getEventsGuests } from "./actions";
import { CustomizableLayout } from "~/types/CustomizableLayout";

const SHOW_MAX_NAMES = 2;
const SHOW_MAX_HEADS = 30;
const HEAD_WIDTH = 39;

export const WhoIsGoing = ({
    event,
    className,
    isManagePage = false,
    customization,
}: {
    event: GetEventFromSlug;
    className?: string;
    isManagePage?: boolean;
    customization?: CustomizableLayout & {
        progressBarEmptyColor?: string;
    };
}) => {
    const [showAll, setShowAll] = useState(false);
    const [canSeeAttendees, setCanSeeAttendees] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const [showMaxHeads, setShowMaxHeads] = useState(SHOW_MAX_HEADS);

    const { data: session } = useSession();
    const permissions = usePermissionsContext();

    const { data: guests } = useQuery(
        ["event-guests", event.id],
        () => getEventsGuests({ eventId: event.id }),
        {
            refetchOnWindowFocus: false,
            refetchOnMount: false,
        },
    );

    const { data: invitesData, isLoading: isInvitesLoading } = useQuery(
        ["proximity-and-followers-invites", event?.id],
        () =>
            getProximityAndFollowersInvites({
                params: {
                    communitySlug: event?.community?.slug,
                    eventSlug: event?.slug,
                    eventId: event?.id,
                },
            }),
        {
            refetchOnWindowFocus: false,
            refetchOnMount: false,
        },
    );

    useEffect(() => {
        if (!containerRef.current) return;

        const calculateMaxHeads = () => {
            const containerWidth = containerRef.current?.clientWidth || 0;
            const maxHeadsToShow = Math.floor(containerWidth / HEAD_WIDTH);
            setShowMaxHeads(Math.max(5, maxHeadsToShow)); // at least 5 heads are shown
        };
        calculateMaxHeads(); // on mount
        window.addEventListener("resize", calculateMaxHeads); // on window resize
        return () => {
            window.removeEventListener("resize", calculateMaxHeads);
        };
    }, []);

    useEffect(() => {
        const isManaging =
            permissions.isHost || permissions.isAdmin || permissions.isManager;

        const isAttendee = !!event.attendees.find(
            (a) => a.user.id === session?.user.id && a.status === "ATTENDING",
        );
        setCanSeeAttendees(isManaging || isAttendee);
    }, [session, permissions, event]);

    const attending =
        event.attendees?.filter(
            (a) =>
                a.status === AttendanceStatus.ATTENDING || Boolean(a.checkedIn),
        ) ?? [];

    const awaiting =
        event.attendees.filter(
            (a) => a.status === AttendanceStatus.PENDING_APPROVAL,
        ) ?? [];

    const waitlist =
        event.attendees.filter((a) => a.status === AttendanceStatus.WAITLIST) ??
        [];

    const awaitingCount = awaiting.length + waitlist.length;

    const attendingUsers =
        attending?.map((attendee) => ({
            id: attendee.user.id,
            name: attendee.user.name,
            image: attendee.user.image,
            slug: attendee.user.slug,
            checkedIn: attendee.checkedIn,
            questionnaireCompletions: attendee.user.questionnaireCompletions,
            ticketPurchase: attendee.user.ticketPurchase,
        })) ?? [];

    const usersAll = dedupe(
        [...event.hosts.map((h) => h.user), ...attendingUsers],
        "id",
    );

    const isAuthorized =
        permissions.isAdmin ||
        permissions.isManager ||
        permissions.isHost ||
        permissions.rbac[PermissionEnum.CHECKIN_ATTENDEES];

    const visibleNames = usersAll
        .slice(0, SHOW_MAX_NAMES)
        .map((attendee) => attendee.name)
        .join(", ");

    const additionalCount = event.attendees.length - SHOW_MAX_NAMES;

    const spotsLeft = event.capacity
        ? Math.max(0, event.capacity - event.attendees.length)
        : "Unlimited";

    return (
        <Card className={cn("flex flex-col gap-5 bg-v3-N-500 p-4", className)}>
            <CardHeader className="flex flex-row items-center justify-between p-0">
                <div className="flex w-full justify-between gap-2">
                    <span
                        className={cn(
                            "text-[20px]",
                            customization?.labelClassName,
                        )}
                    >
                        {usersAll.length} people going
                    </span>

                    {isManagePage && !!isAuthorized && (
                        <div className="flex flex-row gap-2">
                            {canSeeAttendees && (
                                <Button
                                    className="flex gap-2 text-nowrap"
                                    variant="secondary"
                                    size="md"
                                    onClick={() => setShowAll(true)}
                                >
                                    <LuQrCode className="h-3 w-3" />
                                    <span>Checkin Guests</span>
                                </Button>
                            )}
                        </div>
                    )}
                </div>
            </CardHeader>
            <CardContent
                ref={containerRef}
                className="flex w-full flex-wrap gap-3 overflow-x-hidden overflow-y-scroll p-0"
            >
                <CommunityHeads
                    users={usersAll}
                    suffixCounter={Math.max(0, usersAll.length - showMaxHeads)}
                    spaceIcons={false}
                    showMax={showMaxHeads}
                    iconSize={40}
                    revealPII={false}
                    profileLink={true}
                />

                <div className="flex w-full items-center justify-between gap-1">
                    <span className={cn(customization?.labelClassName)}>
                        {visibleNames}
                        {additionalCount > 0 && ` and more`}
                    </span>

                    {canSeeAttendees && visibleNames.length > 0 && (
                        <span
                            className={cn(
                                "cursor-pointer text-nowrap text-secondary",
                                customization?.linkClassName,
                            )}
                            onClick={() => setShowAll(true)}
                        >
                            See all
                        </span>
                    )}
                </div>

                <CardDescription className="flex w-full flex-col gap-2 text-sm text-[#BCC2CC]">
                    <CapacityBar
                        event={event}
                        guests={guests}
                        emptyColor={customization?.progressBarEmptyColor}
                    />
                    <If condition={!event.isExternalEvent}>
                        <div className="flex flex-row">
                            <span
                                className={cn(
                                    "text-sm text-[#BCC2CC]",
                                    customization?.descriptionClassName,
                                )}
                            >
                                {spotsLeft} spots left
                            </span>
                            {waitlist.length > 0 && (
                                <span
                                    className={cn(
                                        "text-sm text-[#BCC2CC]",
                                        customization?.descriptionClassName,
                                    )}
                                >
                                    , {waitlist.length} on waitlist
                                </span>
                            )}
                        </div>
                    </If>

                    {(!!invitesData?.followers.length ||
                        !!invitesData?.eligibleUsers.length) && (
                        <div>
                            You have {invitesData?.followers.length} followers
                            and {invitesData?.eligibleUsers.length} nearby
                            community members you can invite.
                        </div>
                    )}
                </CardDescription>
            </CardContent>

            <EventAttendeesSheet
                isOpen={showAll}
                setIsOpen={setShowAll}
                attendingCount={usersAll.length}
                event={event}
                users={usersAll}
                refetchEvent={() => refetchAction(event.slug)}
                fromBetaPages={true}
            />
        </Card>
    );
};
